import { Button, Card, CardContent, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { RelativeComponentLoader } from "@/components/loader";
import { CopyButton } from "@/components/copyButton";
import dayjs from "dayjs";
import { BiReportsOpportunities } from "@/pages/biReports/biReportsDetailsComponents/BiReportOpportunities";
import { useQuery } from "@tanstack/react-query";
import { getUserByRk } from "@/helpers/apis";
import { getInstanceIdByNameFromDataStores } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { ScrollContainer } from "@/components/scrollContainer";
import { ResourceNotFound } from "@/pages/biReports/errorPage/ResourceNotFound";
import BIUserReportInsightOrOpportunityTag from "@/pages/biReports/BIUserReportInsightOrOpportunityTag";
import { IconChevronLeft } from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

export const BIUserDetailMainContent = ({ id }: { id: string | undefined }) => {
  const { datastores } = useAppState();
  const navigate = useNavigate();
  const { state } = useLocation();
  const userRk = id ?? "";

  // Fetch user data using the new endpoint
  const { data: userDetails, isLoading } = useQuery({
    queryKey: ["GET-BI-USER-BY-RK", userRk],
    queryFn: () => getUserByRk(userRk),
  });

  // Set breadcrumbs with the user's email - use location state as fallback for instant display
  useBreadcrumb(getEntityName(state) ?? userDetails?.email ?? "Loading...", {
    defaultParents: [
      { label: "Insights", pathname: "/bi-reports", noLink: true },
      { label: "Users", pathname: "/bi-reports/users" },
    ],
  });

  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  if (!userDetails) {
    return <ResourceNotFound variant={"user"} />;
  }

  return (
    <ScrollContainer>
      <Stack className="mb-3">
        <Button
          role={"link"}
          variant={"ghost"}
          onClick={() => navigate(-1)}
          className={"hover:al-bg-transparent"}
          aria-label="Go back"
        >
          <IconChevronLeft className={"!al-size-5"} />
        </Button>
        <Stack direction="column" gap="lg" className="ms-2 me-2 w-100">
          <Stack align="center">
            <Typography variant="body" size={"lg"}>
              User:
            </Typography>
            <Typography variant="subheading" className="!al-text-brand">
              {userDetails.email}
            </Typography>
            {userDetails.email && <CopyButton value={userDetails.email} label="Copy Email" />}
          </Stack>
          <Stack gap="md" wrap tag="dl">
            <Stack gap="sm" align="center">
              <dt id="name-label" className="al-text-sm">
                Name:
              </dt>
              <dd
                aria-labelledby="name-label"
                className="al-text-muted-foreground al-text-sm al-m-0"
              >
                {userDetails?.full_name}
              </dd>
            </Stack>
            <Stack gap="sm" align="center">
              <dt id="license-type-label" className="al-text-sm">
                License Type:
              </dt>
              <dd
                aria-labelledby="license-type-label"
                className="al-text-muted-foreground al-text-sm al-m-0"
              >
                {userDetails?.current_license_type}
              </dd>
            </Stack>
            <Stack gap="sm" align="center">
              <dt id="views-label" className="al-text-sm">
                Views:
              </dt>
              <dd
                aria-labelledby="views-label"
                className="al-text-muted-foreground al-text-sm al-m-0"
              >
                {userDetails?.views}
              </dd>
            </Stack>
            <Stack gap="sm" align="center">
              <dt id="dashboards-created-label" className="al-text-sm">
                Dashboards Created:
              </dt>
              <dd
                aria-labelledby="dashboards-created-label"
                className="al-text-muted-foreground al-text-sm al-m-0"
              >
                {userDetails?.dashboards_created}
              </dd>
            </Stack>
            <Stack gap="sm" align="center">
              <dt id="dashboard-consumption-label" className="al-text-sm">
                Dashboard Consumption:
              </dt>
              <dd
                aria-labelledby="dashboard-consumption-label"
                className="al-text-muted-foreground al-text-sm al-m-0"
              >
                {userDetails?.views_received}
              </dd>
            </Stack>
          </Stack>
          <Stack gap="md" wrap tag="dl">
            <Stack gap="sm" align="center">
              <dt id="instance-name-label" className="al-text-sm">
                Instance Name:
              </dt>
              <dd
                aria-labelledby="instance-name-label"
                className="al-text-muted-foreground al-text-sm al-m-0"
              >
                {userDetails?.instance_name}
              </dd>
            </Stack>
            <Stack gap="sm" align="center">
              <dt id="last-activity-label" className="al-text-sm">
                Last Activity:
              </dt>
              {userDetails.last_activity ? (
                <dd
                  aria-labelledby="last-activity-label"
                  className="al-text-muted-foreground al-text-sm al-m-0"
                >
                  {dayjs(userDetails.last_activity).format("DD/MM/YYYY")} (
                  {dayjs(userDetails.last_activity).fromNow()})
                </dd>
              ) : (
                <dd
                  aria-labelledby="last-activity-label"
                  className="al-text-muted-foreground al-text-sm al-m-0"
                >
                  Never
                </dd>
              )}
            </Stack>
          </Stack>
          <Stack justify="start" gap="md" wrap>
            {userDetails.insights.map((insight, index) => (
              <BIUserReportInsightOrOpportunityTag key={index} insightOrOpportunity={insight} />
            ))}
          </Stack>
          <div className="al-mt-1">
            <Card className="al-h-full">
              <CardContent>
                <BiReportsOpportunities
                  instanceId={getInstanceIdByNameFromDataStores(
                    datastores,
                    userDetails.instance_name ?? undefined
                  )}
                  rk={userRk}
                  helperText={
                    "Optimization recommendations to improve license utilization and reduce costs"
                  }
                />
              </CardContent>
            </Card>
          </div>
        </Stack>
      </Stack>
    </ScrollContainer>
  );
};
